/**
 * Hyperlink to an NCR's detail page.
 *
 * The NCR detail lives at `/quality/ncr/<id>/edit` — the same screen the
 * Quality register and the supplier detail's Quality tab navigate to. Keys off
 * the NCR's **primary key**, never the human `ncr_number`.
 *
 * Degrades to plain text when no id resolves, so callers can render this
 * unconditionally — the same contract as PoLineLink / GrnLink.
 *
 * @param ncrId - The NonConformanceReport primary key. Null/undefined renders children as plain text.
 * @param children - The label to render (defaults to the NCR number when given).
 * @param ncrNumber - Optional NCR number used as the default label.
 * @param className - Optional override for the link styling.
 * @returns A router link to the NCR detail, or the bare label.
 *
 * @example
 * <NcrLink ncrId={ncr.id} ncrNumber={ncr.ncr_number} />
 */
import { Link } from 'react-router-dom';
import { ReactNode } from 'react';

interface NcrLinkProps {
  ncrId?: number | string | null;
  ncrNumber?: string | null;
  children?: ReactNode;
  className?: string;
}

/** Canonical deep-link for an NCR detail. */
export function ncrHref(ncrId: number | string): string {
  return `/quality/ncr/${ncrId}/edit`;
}

export default function NcrLink({
  ncrId,
  ncrNumber,
  children,
  className = 'text-primary hover:underline',
}: NcrLinkProps) {
  const label = children ?? ncrNumber ?? '-';
  if (ncrId === null || ncrId === undefined || ncrId === '') {
    return <>{label}</>;
  }
  // Opens in a new tab, consistent with PoLineLink / GrnLink / PoHeaderLink.
  return (
    <Link to={ncrHref(ncrId)} target="_blank" rel="noopener noreferrer" className={className}>
      {label}
    </Link>
  );
}
